'use client'

import type { PricesEntry } from '@cocoonspace/sdk-js/types/pricesEntry'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { UseQueryResult } from '@tanstack/react-query'
import {
	createContext,
	type ReactNode,
	useContext,
	useMemo,
	useState,
} from 'react'

import { MULTIDATE_MIN_AVAILABLE_HOURS } from '../get-available-hours-for-day'
import { usePricesByDates } from '../use-prices-by-dates'

interface MultidatesContextValue {
	space: Space
	value: string[]
	bookingDays: string[]
	setBookingDays: (value: string[]) => void
	onClickBack: () => void
	pricesQuery: UseQueryResult<
		{
			pricesByDay: Record<string, PricesEntry>
			disabled: Date[]
		},
		Error
	>
}

const MultidatesContext = createContext<MultidatesContextValue | undefined>(
	undefined,
)

export const MultidatesProvider = ({
	children,
	space,
	value,
	onClickBack,
}: {
	children: ReactNode
	space: Space
	value: string[]
	onClickBack: () => void
}) => {
	const [bookingDays, setBookingDays] = useState<string[]>(value || [])

	const pricesQuery = usePricesByDates({
		minAvailableHours: MULTIDATE_MIN_AVAILABLE_HOURS,
		onlyEmptyDays: true,
		space,
	})

	const providerValue = useMemo(
		() => ({
			onClickBack,
			space,
			value,
		}),
		[space, value, onClickBack],
	)

	return (
		<MultidatesContext.Provider
			value={{
				...providerValue,
				bookingDays,
				pricesQuery,
				setBookingDays,
			}}
		>
			{children}
		</MultidatesContext.Provider>
	)
}

export const useMultidatesContext = () => {
	const context = useContext(MultidatesContext)

	if (!context) {
		throw new Error('useMultidates must be used within a MultidatesProvider')
	}

	return context
}
